import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  CalendarDays,
  CheckCircle,
  ClipboardCopy,
  Compass,
  Download,
  FileDown,
  Home,
  Info,
  Layers,
  ListChecks,
  Map,
  PackageSearch,
  Printer,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Target,
  UserRound,
} from 'lucide-react';
import axios from 'axios';

import { API } from '../config';

const severityRank = { high: 3, medium: 2, low: 1 };

const titleCase = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

const formatDate = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const scoreTone = (score) => {
  if (score >= 85) return { label: 'Excellent', text: 'Strong Vastu alignment', color: '#15803d' };
  if (score >= 70) return { label: 'Good', text: 'Good layout with a few corrections', color: '#0f766e' };
  if (score >= 55) return { label: 'Needs Work', text: 'Several improvements recommended', color: '#b45309' };
  return { label: 'Critical Review', text: 'Priority corrections recommended', color: '#b91c1c' };
};

const getSeverityStyles = (severity) => {
  const key = String(severity || 'low').toLowerCase();
  if (key === 'high') return 'border-red-300 bg-red-50 text-red-900';
  if (key === 'medium') return 'border-amber-300 bg-amber-50 text-amber-900';
  if (key === 'low') return 'border-sky-300 bg-sky-50 text-sky-900';
  return 'border-slate-300 bg-slate-50 text-slate-800';
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const collectRooms = (analysisResult) => {
  const detailed = analysisResult?.detailed_analysis || {};
  if (Array.isArray(detailed.detected_rooms)) return detailed.detected_rooms;
  if (Array.isArray(detailed.rooms)) return detailed.rooms;
  if (Array.isArray(detailed?.room_detection?.rooms)) return detailed.room_detection.rooms;
  return [];
};

const isRenderableDetail = ([key, value]) => {
  if (value === null || value === undefined || value === '') return false;
  if (['detected_rooms', 'rooms', 'room_detection'].includes(key)) return false;
  return true;
};

const renderDetailValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-[#777777]">Not available</span>;
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <p className="text-sm leading-6 text-[#353535]">{String(value)}</p>;
  }

  if (Array.isArray(value)) {
    return (
      <ul className="space-y-2">
        {value.map((item, index) => (
          <li key={index} className="flex gap-2 text-sm leading-6 text-[#353535]">
            <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-[#232323]" />
            <span>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (typeof value === 'object') {
    return (
      <div className="grid gap-3">
        {Object.entries(value)
          .filter(([, nestedValue]) => nestedValue !== null && nestedValue !== undefined && nestedValue !== '')
          .map(([nestedKey, nestedValue]) => (
            <div key={nestedKey} className="rounded-lg border border-[#e3e0d8] bg-white p-3">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#666666]">
                {titleCase(nestedKey)}
              </div>
              {renderDetailValue(nestedValue)}
            </div>
          ))}
      </div>
    );
  }

  return <p className="text-sm text-[#353535]">{String(value)}</p>;
};

const zoneOrder = ['NW', 'N', 'NE', 'W', 'CENTER', 'E', 'SW', 'S', 'SE'];

const Report = () => {
  const { id } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        setError('');
        const response = await axios.get(`${API}/vastu/analysis/${id}`, {
          withCredentials: true,
        });
        setAnalysis(response.data);
      } catch (fetchError) {
        console.error('Error fetching analysis:', fetchError);
        setError(fetchError?.response?.data?.detail || 'Unable to load this report.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [id]);

  const report = analysis?.analysis_result || {};
  const score = Number(report.overall_score ?? 0);
  const tone = scoreTone(score);
  const strengths = safeArray(report.strengths);
  const issues = safeArray(report.issues).sort(
    (a, b) => (severityRank[String(b.severity || '').toLowerCase()] || 0) - (severityRank[String(a.severity || '').toLowerCase()] || 0)
  );
  const detailedEntries = Object.entries(report.detailed_analysis || {}).filter(isRenderableDetail);
  const rooms = useMemo(() => collectRooms(report), [report]);

  const severityCounts = useMemo(() => {
    return issues.reduce(
      (acc, issue) => {
        const key = String(issue.severity || 'low').toLowerCase();
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      { high: 0, medium: 0, low: 0 }
    );
  }, [issues]);

  const roomsByZone = useMemo(() => {
    return rooms.reduce((acc, room) => {
      const zone = String(room.vastu_zone || 'CENTER').toUpperCase();
      acc[zone] = acc[zone] || [];
      acc[zone].push(room);
      return acc;
    }, {});
  }, [rooms]);

  const topPriorityIssues = issues.slice(0, 3);
  const remedies = issues
    .map((issue) => ({
      zone: issue.zone || 'General',
      severity: issue.severity || 'low',
      remedy: issue.remedy,
    }))
    .filter((item) => item.remedy);

  const handleDownloadPdf = () => {
    if (!analysis || downloadingPdf) return;
    setDownloadingPdf(true);
    const originalTitle = document.title;
    const fileSafeId = (id || 'vastu-report').replace(/[^a-zA-Z0-9-_]/g, '');
    document.title = `Vastu-Report-${fileSafeId}`;
    window.print();
    window.setTimeout(() => {
      document.title = originalTitle;
      setDownloadingPdf(false);
    }, 500);
  };

  const handleDownloadJson = () => {
    if (!analysis) return;
    const blob = new Blob([JSON.stringify(analysis, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vastu-report-${id}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopySummary = async () => {
    if (!analysis) return;
    const summary = [
      `Vastu Report: ${analysis.id}`,
      `Property: ${titleCase(analysis.property_type)}`,
      `Facing: ${titleCase(analysis.orientation)}`,
      `Score: ${score}/100 (${tone.label})`,
      `Priority issues: ${severityCounts.high} high, ${severityCounts.medium} medium, ${severityCounts.low} low`,
      topPriorityIssues.length
        ? `Top issue: ${topPriorityIssues[0].zone || 'General'} - ${topPriorityIssues[0].issue || 'Review recommended'}`
        : 'No major issues listed.',
    ].join('\n');

    try {
      await navigator.clipboard.writeText(summary);
      setCopyStatus('Copied');
      window.setTimeout(() => setCopyStatus(''), 1600);
    } catch (copyError) {
      setCopyStatus('Copy failed');
      window.setTimeout(() => setCopyStatus(''), 1600);
    }
  };

  if (loading) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <RefreshCw className="mx-auto mb-4 h-10 w-10 animate-spin text-[#232323]" />
          <p className="text-[#353535]">Preparing your full Vastu report...</p>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="main-content min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <AlertTriangle className="mx-auto mb-4 h-10 w-10 text-red-700" />
          <h2 className="mb-3 text-2xl font-bold text-[#232323]">Report Not Found</h2>
          <p className="mb-6 text-[#353535]">{error || 'The requested report could not be found.'}</p>
          <Link to="/upload" className="btn-primary">
            Upload New Floor Plan
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content min-h-screen bg-[#f6f3ec] px-4 py-8 report-print-root">
      <div className="mx-auto max-w-7xl">
        <div className="report-print-hide mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#666666]">Complete Vastu Audit</p>
            <h1 className="text-3xl font-bold text-[#232323]">Detailed Vastu Report</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={handleCopySummary} className="report-tool-button">
              <ClipboardCopy className="h-4 w-4" />
              {copyStatus || 'Copy Summary'}
            </button>
            <button onClick={handleDownloadJson} className="report-tool-button">
              <Download className="h-4 w-4" />
              Export JSON
            </button>
            <button onClick={handleDownloadPdf} disabled={downloadingPdf} className="report-tool-button report-tool-button-dark">
              <FileDown className="h-4 w-4" />
              {downloadingPdf ? 'Preparing PDF' : 'Download PDF'}
            </button>
          </div>
        </div>

        <div className="report-sheet">
          <section className="report-hero">
            <div className="report-hero-copy">
              <div className="mb-4 flex flex-wrap gap-2">
                <span className="report-chip">
                  <Home className="h-3.5 w-3.5" />
                  {titleCase(analysis.property_type)}
                </span>
                <span className="report-chip">
                  <Compass className="h-3.5 w-3.5" />
                  {titleCase(analysis.orientation)} Facing
                </span>
                <span className="report-chip">
                  <CalendarDays className="h-3.5 w-3.5" />
                  {formatDate(analysis.created_at)}
                </span>
              </div>
              <h2 className="mb-3 text-4xl font-bold leading-tight text-[#232323]">Vastu Compliance Report</h2>
              <p className="max-w-3xl text-sm leading-6 text-[#353535]">
                This report combines the detected layout information, room evidence, directional assessment, practical remedies,
                and a prioritized action plan for improving the property.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <MetricCard label="Report ID" value={analysis.id?.slice(0, 8) || 'N/A'} icon={ShieldCheck} />
                <MetricCard label="Rooms Reviewed" value={rooms.length || 'N/A'} icon={Layers} />
                <MetricCard label="Issues Found" value={issues.length} icon={AlertTriangle} />
              </div>
            </div>

            <div className="report-score-panel">
              <div
                className="report-score-ring"
                style={{
                  '--score-color': tone.color,
                  '--score-angle': `${Math.max(0, Math.min(100, score)) * 3.6}deg`,
                }}
              >
                <div className="report-score-inner">
                  <span className="text-5xl font-bold text-[#232323]">{score}</span>
                  <span className="text-xs font-semibold uppercase text-[#666666]">out of 100</span>
                </div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-xl font-bold" style={{ color: tone.color }}>
                  {tone.label}
                </h3>
                <p className="text-sm text-[#353535]">{tone.text}</p>
              </div>
            </div>
          </section>

          <section className="report-section">
            <div className="report-section-heading">
              <BarChart3 className="h-5 w-5 text-[#232323]" />
              <div>
                <h2>Executive Summary</h2>
                <p>High-level reading of compliance, strengths, issues, and urgency.</p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-4">
              <SummaryTile label="Overall Score" value={`${score}/100`} note={tone.label} />
              <SummaryTile label="High Priority" value={severityCounts.high} note="Needs immediate review" tone="danger" />
              <SummaryTile label="Medium Priority" value={severityCounts.medium} note="Plan practical corrections" tone="warn" />
              <SummaryTile label="Positive Aspects" value={strengths.length} note="Keep these preserved" tone="good" />
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-[#e4dfd5] bg-[#fbfaf7] p-5">
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-[#232323]">
                  <CheckCircle className="h-4 w-4 text-green-700" />
                  Key Strengths
                </h3>
                {strengths.length ? (
                  <ul className="space-y-3">
                    {strengths.map((strength, index) => (
                      <li key={index} className="flex gap-3 text-sm leading-6 text-[#353535]">
                        <BadgeCheck className="mt-0.5 h-4 w-4 flex-none text-green-700" />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyText text="No strengths were explicitly listed in this analysis." />
                )}
              </div>

              <div className="rounded-xl border border-[#e4dfd5] bg-[#fbfaf7] p-5">
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-[#232323]">
                  <Target className="h-4 w-4 text-[#b45309]" />
                  Top Priorities
                </h3>
                {topPriorityIssues.length ? (
                  <div className="space-y-3">
                    {topPriorityIssues.map((issue, index) => (
                      <div key={index} className={`rounded-lg border p-3 ${getSeverityStyles(issue.severity)}`}>
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="font-semibold">{issue.zone || 'General'}</span>
                          <span className="text-xs font-bold uppercase">{issue.severity || 'review'}</span>
                        </div>
                        <p className="text-sm leading-6">{issue.issue || 'Review recommended.'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyText text="No priority issues were found in this report." />
                )}
              </div>
            </div>
          </section>

          <section className="report-section">
            <div className="report-section-heading">
              <Map className="h-5 w-5 text-[#232323]" />
              <div>
                <h2>Vastu Zone Overview</h2>
                <p>Directional room placement from detected or submitted room evidence.</p>
              </div>
            </div>
            <div className="report-zone-grid">
              {zoneOrder.map((zone) => {
                const zoneRooms = roomsByZone[zone] || [];
                return (
                  <div key={zone} className={`report-zone-cell ${zone === 'CENTER' ? 'report-zone-center' : ''}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="font-bold text-[#232323]">{zone}</span>
                      <span className="text-xs text-[#666666]">{zoneRooms.length} room{zoneRooms.length === 1 ? '' : 's'}</span>
                    </div>
                    {zoneRooms.length ? (
                      <div className="space-y-1">
                        {zoneRooms.slice(0, 4).map((room, index) => (
                          <div key={`${zone}-${index}`} className="truncate rounded-md bg-white px-2 py-1 text-xs text-[#353535]">
                            {room.type || 'Unknown Room'}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-[#888888]">No room data</span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="report-section">
            <div className="report-section-heading">
              <AlertTriangle className="h-5 w-5 text-[#232323]" />
              <div>
                <h2>Issues And Remedies</h2>
                <p>Every issue returned by the analysis, ordered by priority.</p>
              </div>
            </div>
            {issues.length ? (
              <div className="grid gap-4">
                {issues.map((issue, index) => (
                  <article key={index} className={`rounded-xl border p-5 ${getSeverityStyles(issue.severity)}`}>
                    <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="text-lg font-bold">{issue.zone || `Issue ${index + 1}`}</h3>
                        <p className="text-xs font-semibold uppercase tracking-wide">{issue.severity || 'Review'} Priority</p>
                      </div>
                      <span className="rounded-full bg-white/70 px-3 py-1 text-xs font-semibold">#{index + 1}</span>
                    </div>
                    <div className="grid gap-4 lg:grid-cols-2">
                      <div>
                        <h4 className="mb-1 text-sm font-bold">Observation</h4>
                        <p className="text-sm leading-6">{issue.issue || 'No issue detail provided.'}</p>
                      </div>
                      <div>
                        <h4 className="mb-1 text-sm font-bold">Recommended Remedy</h4>
                        <p className="text-sm leading-6">{issue.remedy || 'Consult an expert for a specific remedy.'}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyText text="No issues were returned for this report." />
            )}
          </section>

          <section className="report-section">
            <div className="report-section-heading">
              <ListChecks className="h-5 w-5 text-[#232323]" />
              <div>
                <h2>Action Plan</h2>
                <p>Suggested order of implementation based on issue severity.</p>
              </div>
            </div>
            <div className="grid gap-4 lg:grid-cols-3">
              <ActionColumn
                title="Immediate"
                subtitle="High priority corrections"
                issues={issues.filter((issue) => String(issue.severity || '').toLowerCase() === 'high')}
              />
              <ActionColumn
                title="Next 30 Days"
                subtitle="Medium priority improvements"
                issues={issues.filter((issue) => String(issue.severity || '').toLowerCase() === 'medium')}
              />
              <ActionColumn
                title="Maintenance"
                subtitle="Low priority and habit changes"
                issues={issues.filter((issue) => String(issue.severity || 'low').toLowerCase() === 'low')}
              />
            </div>
          </section>

          <section className="report-section">
            <div className="report-section-heading">
              <Layers className="h-5 w-5 text-[#232323]" />
              <div>
                <h2>Room Evidence</h2>
                <p>Detected or submitted room data used to support the report.</p>
              </div>
            </div>
            {rooms.length ? (
              <div className="overflow-hidden rounded-xl border border-[#e4dfd5]">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#f2eee6] text-xs uppercase tracking-wide text-[#666666]">
                    <tr>
                      <th className="px-4 py-3">Room</th>
                      <th className="px-4 py-3">Zone</th>
                      <th className="px-4 py-3">Confidence</th>
                      <th className="px-4 py-3">Geometry</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#ece6dd] bg-white">
                    {rooms.map((room, index) => (
                      <tr key={room.id || index}>
                        <td className="px-4 py-3 font-semibold text-[#232323]">{room.type || 'Unknown Room'}</td>
                        <td className="px-4 py-3 text-[#353535]">{room.vastu_zone || 'CENTER'}</td>
                        <td className="px-4 py-3 text-[#353535]">
                          {typeof room.confidence === 'number' ? `${Math.round(room.confidence * 100)}%` : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-[#353535]">
                          {Math.round(Number(room.width || 0))} x {Math.round(Number(room.height || 0))}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyText text="No room evidence was stored with this report. Future reports can include detailed detected-room metadata." />
            )}
          </section>

          <section className="report-section">
            <div className="report-section-heading">
              <Info className="h-5 w-5 text-[#232323]" />
              <div>
                <h2>Detailed Analysis</h2>
                <p>Room-by-room and topic-wise observations from the analysis engine.</p>
              </div>
            </div>
            {detailedEntries.length ? (
              <div className="grid gap-4">
                {detailedEntries.map(([key, value]) => (
                  <div key={key} className="rounded-xl border border-[#e4dfd5] bg-[#fbfaf7] p-5">
                    <h3 className="mb-3 text-lg font-bold text-[#232323]">{titleCase(key)}</h3>
                    {renderDetailValue(value)}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyText text="No detailed analysis sections were returned." />
            )}
          </section>

          <section className="report-section">
            <div className="report-section-heading">
              <Sparkles className="h-5 w-5 text-[#232323]" />
              <div>
                <h2>Remedy Checklist</h2>
                <p>Practical remedies extracted from the report for quick execution.</p>
              </div>
            </div>
            {remedies.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                {remedies.map((item, index) => (
                  <div key={index} className="rounded-xl border border-[#e4dfd5] bg-white p-4">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <span className="font-semibold text-[#232323]">{item.zone}</span>
                      <span className="rounded-full bg-[#f2eee6] px-2 py-1 text-xs uppercase text-[#666666]">{item.severity}</span>
                    </div>
                    <p className="text-sm leading-6 text-[#353535]">{item.remedy}</p>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyText text="No remedy checklist items were returned." />
            )}
          </section>

          <section className="report-section report-print-hide">
            <div className="grid gap-4 md:grid-cols-3">
              <Link to="/marketplace" className="report-action-card">
                <PackageSearch className="h-6 w-6 text-[#232323]" />
                <h3>Shop Remedies</h3>
                <p>Browse Vastu correction products and tools.</p>
                <span>
                  View Marketplace <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
              <Link to="/consultants" className="report-action-card">
                <UserRound className="h-6 w-6 text-[#232323]" />
                <h3>Talk To Expert</h3>
                <p>Get personalized advice from consultants.</p>
                <span>
                  Book Consultation <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
              <button onClick={handleDownloadPdf} className="report-action-card text-left">
                <Printer className="h-6 w-6 text-[#232323]" />
                <h3>Save Report</h3>
                <p>Download this full report as a PDF through browser print.</p>
                <span>
                  Print / PDF <ArrowRight className="h-4 w-4" />
                </span>
              </button>
            </div>
          </section>

          <footer className="report-footer">
            <p>
              Generated by Vastuspace.AI. This report is intended as a planning aid and should be reviewed with practical
              site conditions, structural constraints, and expert judgment.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ label, value, icon: Icon }) => (
  <div className="rounded-xl border border-white/50 bg-white/70 p-4 shadow-sm">
    <Icon className="mb-2 h-5 w-5 text-[#232323]" />
    <div className="text-xs uppercase tracking-wide text-[#666666]">{label}</div>
    <div className="mt-1 truncate text-lg font-bold text-[#232323]">{value}</div>
  </div>
);

const SummaryTile = ({ label, value, note, tone = 'neutral' }) => (
  <div className={`report-summary-tile report-summary-${tone}`}>
    <div className="text-xs font-semibold uppercase tracking-wide text-[#666666]">{label}</div>
    <div className="mt-2 text-3xl font-bold text-[#232323]">{value}</div>
    <div className="mt-1 text-sm text-[#353535]">{note}</div>
  </div>
);

const EmptyText = ({ text }) => (
  <div className="rounded-xl border border-dashed border-[#d8d1c3] bg-[#fbfaf7] p-5 text-sm text-[#666666]">{text}</div>
);

const ActionColumn = ({ title, subtitle, issues }) => (
  <div className="rounded-xl border border-[#e4dfd5] bg-[#fbfaf7] p-5">
    <h3 className="text-lg font-bold text-[#232323]">{title}</h3>
    <p className="mb-4 text-sm text-[#666666]">{subtitle}</p>
    {issues.length ? (
      <ul className="space-y-3">
        {issues.map((issue, index) => (
          <li key={index} className="rounded-lg bg-white p-3 text-sm leading-6 text-[#353535]">
            <strong>{issue.zone || 'General'}:</strong> {issue.remedy || issue.issue || 'Review recommended.'}
          </li>
        ))}
      </ul>
    ) : (
      <p className="rounded-lg bg-white p-3 text-sm text-[#777777]">No items in this priority band.</p>
    )}
  </div>
);

export default Report;
